import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, InputBase } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { useNewBreadcrumb } from 'recoils';
import { EsgSingleSelectControlledField, Label14Medium, MetricCard, ReturnButton } from 'components';
import { useNotify } from 'hooks';
import {
    useGetInitiative,
    useUpdateInitiative,
    useInitiativeTypes,
    useInitiativeCategories,
    useInitiativeKpiOptions,
    useInitiativeKpiUnits,
} from 'hooks/api/initiatives';
import { useGetUsers } from 'hooks/api/users';
import { useGetAllowedValues } from 'hooks/api/allowed-values';
import type { InitiativeStatus } from 'api/initiatives/types';
import {
    InitiativeCapexIcon,
    InitiativeAnnualIcon,
    InitiativePaybackIcon,
    InitiativeKpiIcon,
    InitiativeRoiIcon,
    InitiativeNpvIcon,
} from 'assets';
import { formatCurrency, formatLocaleNumber, formatNullable } from 'utils';

import { INITIATIVES_PAGE } from '../consts';
import { DetailFormState, DerivedValues } from './types';
import { STATUS_OPTIONS } from './consts';
import {
    OverviewSection,
    TimelineSection,
    FinancialSection,
    KpiSection,
    CostEfficiencyCard,
} from './components';
import {
    calcNetAnnualBenefit,
    calcSimplePayback,
    calcRoi,
    calcNpv,
    calcCostPerKpiUnit,
    calcProjectedImprovementPct,
    calcProjectedNewKpiValue,
} from './helpers';

export const InitiativeDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const notify = useNotify();
    const initiativeId = Number(id);

    useNewBreadcrumb(INITIATIVES_PAGE.SIDEBAR_TITLE, INITIATIVES_PAGE.ABSOLUTE_PATH);

    const { data: initiative, isLoading, isError } = useGetInitiative(initiativeId);
    const { mutateAsync: saveInitiative, isLoading: isSaving } = useUpdateInitiative();
    const { data: initiativeTypes = [] } = useInitiativeTypes();
    const { data: categories = [] } = useInitiativeCategories();
    const { data: kpiOptions = [], isLoading: loadingKpi } = useInitiativeKpiOptions();
    const { data: kpiUnitsData = {}, isLoading: loadingUnits } = useInitiativeKpiUnits();
    const { data: users = [] } = useGetUsers();
    const { data: allowedValues } = useGetAllowedValues();

    const [form, setForm] = useState<DetailFormState | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const lastInitializedId = useRef<number | null>(null);
    const initialForm = useRef<DetailFormState | null>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (initiative && lastInitializedId.current !== initiativeId) {
            lastInitializedId.current = initiativeId;
            setIsDirty(false);
            const snapshot: DetailFormState = {
                name: initiative.name,
                description: initiative.description ?? '',
                category: initiative.category,
                type: initiative.type,
                ownerIds: initiative.owner.map(String),
                level: initiative.level,
                levelEntity: initiative.level_entity ?? '',
                startDate: initiative.start_date,
                endDate: initiative.end_date ?? '',
                status: initiative.status,
                priority: initiative.priority,
                financeApprovalRecorded: initiative.finance_approval_recorded ?? false,
                capex: initiative.capex,
                annualOpexChange: initiative.annual_opex_change,
                annualGrossSavings: initiative.annual_gross_savings,
                annualTco2eAvoided: initiative.annualTco2eAvoided ?? null,
                projectLifespan: initiative.project_lifespan,
                discountRate: initiative.discount_rate,
                kpiName: initiative.kpi,
                unit: initiative.unit ?? '',
                baselineValue: initiative.baseline_value,
                expectedAnnualImprovement: initiative.expected_annual_improvement,
                direction: initiative.direction ?? 'REDUCTION',
            };
            initialForm.current = snapshot;
            setForm(snapshot);
        }
    }, [initiative, initiativeId]);

    const updateField = useCallback(
        <K extends keyof DetailFormState>(field: K, value: DetailFormState[K]) => {
            setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
            setIsDirty(true);
        },
        [],
    );

    const handleKpiNameChange = useCallback((value: string) => {
        setForm((prev) => (prev ? { ...prev, kpiName: value, unit: '' } : prev));
        setIsDirty(true);
    }, []);

    const handleCancel = useCallback(() => {
        navigate(INITIATIVES_PAGE.ABSOLUTE_PATH);
    }, [navigate]);

    const handleReset = useCallback(() => {
        if (initialForm.current) {
            setForm(initialForm.current);
            setIsDirty(false);
        }
    }, []);

    const handleSave = useCallback(async () => {
        if (!form) return;
        try {
            await saveInitiative({
                id: initiativeId,
                body: {
                    name: form.name.trim(),
                    description: form.description || null,
                    category: form.category,
                    type: form.type,
                    owner: form.ownerIds.map(Number),
                    level: form.level,
                    level_entity: form.level !== 'GLOBAL' && form.levelEntity ? form.levelEntity : null,
                    start_date: form.startDate,
                    end_date: form.endDate || null,
                    status: form.status,
                    priority: form.priority,
                    finance_approval_recorded: form.financeApprovalRecorded,
                    capex: form.capex,
                    annual_opex_change: form.annualOpexChange,
                    annual_gross_savings: form.annualGrossSavings,
                    annualTco2eAvoided: form.annualTco2eAvoided,
                    project_lifespan: form.projectLifespan,
                    discount_rate: form.discountRate,
                    kpi: form.kpiName,
                    unit: form.unit || null,
                    baseline_value: form.baselineValue,
                    expected_annual_improvement: form.expectedAnnualImprovement,
                    direction: form.direction,
                },
            });
            setIsDirty(false);
            notify.success('Initiative updated successfully!');
        } catch {
            notify.error('Failed to update initiative. Please try again.');
        }
    }, [form, initiativeId, saveInitiative, notify]);

    // ── Derived option lists ──────────────────────────────────────────────────

    const typeOptions = initiativeTypes.map((t) => ({ label: t.typeName, value: t.typeName }));
    const kpiSelectOptions = kpiOptions.map((k) => ({ label: k.kpiName, value: k.kpiName }));
    const unitSelectOptions = (kpiUnitsData[form?.kpiName ?? ''] ?? []).map((u: string) => ({ label: u, value: u }));
    const categoryOptions = categories.map((c) => ({ label: c.categoryName, value: c.categoryName }));
    const ownerOptions = users.map((u) => {
        const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
        return { label: name, value: String(u.id), name };
    });

    // ── Real-time financial & KPI calculations ────────────────────────────────

    const derived = useMemo((): DerivedValues | null => {
        if (!form) return null;
        const nab = calcNetAnnualBenefit(form.annualGrossSavings, form.annualOpexChange);
        const simplePayback = calcSimplePayback(form.capex, nab);
        const npv = calcNpv(nab, form.capex, form.projectLifespan, form.discountRate);
        const roi = calcRoi(npv, form.capex);
        const costPerKpiUnit = calcCostPerKpiUnit(form.capex, form.expectedAnnualImprovement);
        const projectedImprovementPct = calcProjectedImprovementPct(
            form.expectedAnnualImprovement,
            form.baselineValue,
        );
        const projectedNewKpiValue = calcProjectedNewKpiValue(
            form.baselineValue,
            form.expectedAnnualImprovement,
            form.direction,
        );
        return { nab, simplePayback, roi, npv, costPerKpiUnit, projectedImprovementPct, projectedNewKpiValue };
    }, [form]);

    // ── Metric summary cards (real-time) ──────────────────────────────────────

    const metricCards = useMemo(() => [
        {
            badge: 'CapEx',
            badgeIcon: <InitiativeCapexIcon width={12} height={12} />,
            value: formatNullable(form?.capex ?? null, formatCurrency),
            subtitle: 'Total Investment',
            tooltip: {
                title: "Total Investment (CapEx)",
                text: "The one-time capital cost to set up this initiative. Mirrors the CapEx input below.",
                chip: "input",
            },
        },
        {
            badge: 'Annual',
            badgeIcon: <InitiativeAnnualIcon width={14} height={12} />,
            value: formatNullable(derived?.nab ?? null, formatCurrency),
            subtitle: 'Net Annual Benefit',
            tooltip: {
                title: "Net Annual Benefit",
                text: "The yearly cash benefit, after accounting for any change in operating costs.",
                formula: `Annual Gross Savings - Annual OpEx Change`,
                chip: "Calculated",
            },
        },
        {
            badge: 'Payback',
            badgeIcon: <InitiativePaybackIcon width={12} height={12} />,
            value: formatNullable(derived?.simplePayback ?? null, (v) => `${formatLocaleNumber(v)} yrs`),
            subtitle: 'Simple Payback Period',
            tooltip: {
                title: "Simple Payback Period",
                text: "How long it takes for the yearly benefit to cover the upfront cost. Doesn't adjust for the time value of money.",
                formula: "CapEx ÷ Net Annual Benefit",
                chip: "Calculated",
            },
        },
        {
            badge: 'KPI',
            badgeIcon: <InitiativeKpiIcon width={12} height={12} />,
            value: formatNullable(derived?.costPerKpiUnit ?? null, formatCurrency),
            subtitle: 'Cost per KPI Unit',
            tooltip: {
                title: "Cost per KPI Unit",
                text: "How much capital is spent for each unit of KPI improvement per year. Lower is more efficient.",
                formula: "CapEx ÷ Expected Annual Improvement",
                chip: "Calculated",
            },
        },
        {
            badge: 'ROI (Discounted)',
            badgeIcon: <InitiativeRoiIcon width={9} height={12} />,
            value: formatNullable(derived?.roi ?? null, (v) => `${formatLocaleNumber(v)}%`),
            subtitle: 'Return on Investment',
            tooltip: {
                title: "Return on Investment (Discounted)",
                text: "The overall return over the project's lifetime, adjusted so future cash is worth less than cash today.",
                formula: ["(Σ discounted Net Benefits - CapEx) ÷ CapEx", "where each year's benefit = Net Benefit ÷ (1+r)ᵗ"],
                chip: "Calculated",
            },
        },
        {
            badge: 'NPV',
            badgeIcon: <InitiativeNpvIcon width={12} height={12} />,
            value: formatNullable(derived?.npv ?? null, formatCurrency),
            subtitle: 'Net Present Value',
            tooltip: {
                title: "Net Present Value (NPV)",
                text: "The total financial value of this initiative in today's money. Positive means it creates value; negative means it costs more than it returns.",
                formula: ["Σ (Net Annual Benefit ÷ (1+r)ᵗ) - CapEx", "t = 1 … Project Lifespan"],
                chip: "Calculated",
            },
        },
    ], [form, derived]);

    // ── Guard renders ─────────────────────────────────────────────────────────

    if (isError || (!isLoading && !initiative)) {
        return (
            <Box sx={{ p: 3 }}>
                <Label14Medium color="error.main">Failed to load initiative. Please try again.</Label14Medium>
            </Box>
        );
    }

    if (isLoading || !form) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Sticky sub-header */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: 'white',
                    borderBottom: '1px solid #E2E8F0',
                    px: 3,
                    pt: 1.5,
                    pb: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: "24px",
                }}
            >
                <ReturnButton label="Back to Initiatives Overview" onClick={handleCancel} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    {/* Auto-width title input + pencil affordance */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            minWidth: 0,
                            '&:hover .title-edit-icon': { opacity: 1 },
                        }}
                    >
                        {/* CSS grid trick: container sizes to text width */}
                        <Box
                            data-value={form.name || ' '}
                            sx={{
                                display: 'inline-grid',
                                minWidth: '120px',
                                maxWidth: '600px',
                                '&::after': {
                                    content: 'attr(data-value) " "',
                                    visibility: 'hidden',
                                    gridArea: '1 / 1',
                                    whiteSpace: 'pre',
                                    fontSize: '30px',
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    pointerEvents: 'none',
                                },
                            }}
                        >
                            <InputBase
                                inputRef={nameInputRef}
                                value={form.name}
                                onChange={(e) => updateField('name', e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') nameInputRef.current?.blur(); }}
                                onBlur={() => {
                                    if (!form.name.trim() && initialForm.current?.name) {
                                        updateField('name', initialForm.current.name);
                                    }
                                }}
                                inputProps={{ maxLength: 255 }}
                                sx={{
                                    gridArea: '1 / 1',
                                    fontSize: '30px',
                                    fontWeight: 700,
                                    color: '#1A4252',
                                    lineHeight: 1.3,
                                    borderBottom: '1px solid transparent',
                                    borderRadius: 0,
                                    transition: 'border-color 0.15s',
                                    '& input': { p: 0 },
                                    '&:hover': { borderBottom: '1px dashed #E4E7EC' },
                                    '&.Mui-focused': { borderBottom: '1px solid #0097B2' },
                                }}
                            />
                        </Box>
                        <EditOutlinedIcon
                            className="title-edit-icon"
                            onClick={() => nameInputRef.current?.focus()}
                            sx={{
                                opacity: 0,
                                fontSize: '18px',
                                color: '#98A2B3',
                                transition: 'opacity 0.15s',
                                cursor: 'pointer',
                                flexShrink: 0,
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EsgSingleSelectControlledField
                            options={STATUS_OPTIONS}
                            selectedValue={form.status}
                            setSelectedValue={(v) => updateField('status', v as InitiativeStatus)}
                        />
                        <Button
                            variant="outlined" size="small"
                            onClick={handleReset}
                            disabled={isSaving || !isDirty}
                            sx={{ borderRadius: 2, height: 36 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained" size="small"
                            disabled={!isDirty || isSaving}
                            onClick={handleSave}
                            sx={{
                                borderRadius: 2,
                                height: 36,
                                backgroundColor: '#68AC39',
                                '&:hover': { backgroundColor: '#5a9430' },
                                '&.Mui-disabled': { backgroundColor: '#68AC39', opacity: 0.45 },
                            }}
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                    {metricCards.map((card) => (
                        <MetricCard
                            key={card.badge}
                            badge={card.badge}
                            badgeIcon={card.badgeIcon}
                            value={card.value}
                            subtitle={card.subtitle}
                            tooltip={card.tooltip}
                        />
                    ))}
                </Box>

                <OverviewSection
                    form={form}
                    updateField={updateField}
                    categoryOptions={categoryOptions}
                    typeOptions={typeOptions}
                    ownerOptions={ownerOptions}
                    allowedValues={allowedValues}
                />
                <TimelineSection form={form} updateField={updateField} />
                <FinancialSection form={form} updateField={updateField} derived={derived} initiative={initiative} />
                <KpiSection
                    form={form}
                    updateField={updateField}
                    onKpiNameChange={handleKpiNameChange}
                    derived={derived}
                    kpiSelectOptions={kpiSelectOptions}
                    loadingKpi={loadingKpi}
                    unitSelectOptions={unitSelectOptions}
                    loadingUnits={loadingUnits}
                />
                <CostEfficiencyCard derived={derived} unit={form.unit} />
            </Box>
        </Box>
    );
};
